import { ApiClient } from './api-client';

export interface MeProfile {
  id: string;
  /** Public privacy handle (V49) — shown to other users in conversations. */
  username?: string;
  /** Nullable since the simplified signup flow lets users register with
   *  email + password only. Account-settings forms convert null to "" for
   *  the controlled inputs and convert back on save. */
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  phoneNumber?: string | null;
  emailVerified: boolean;
  emailVerifiedAt?: string | null;
  phoneVerified: boolean;
  phoneVerifiedAt?: string | null;
  roles: string[];
  permissions: string[];
  /** False for OAuth-only rows (e.g. Google sign-in without a local
   *  password). The self-service delete flow hides the password
   *  confirmation field when this is false. */
  hasPassword: boolean;
}

export interface UpdateMePayload {
  firstName?: string;
  lastName?: string;
  /** New public username (V49). Omit (undefined) to leave unchanged; never
   *  send "" — the backend rejects a malformed value. */
  username?: string;
  /** Changing the phone number resets phoneVerified — the user will need
   *  to go through phone verification again. */
  phoneNumber?: string;
}

export const usersService = {
  getMe: async (): Promise<MeProfile> =>
    (await ApiClient.get<MeProfile>('/users/me')).data,

  updateMe: async (payload: UpdateMePayload): Promise<MeProfile> =>
    (await ApiClient.patch<MeProfile>('/users/me', payload)).data,

  // Self-service soft-delete. The backend revokes every session on
  // success, so the caller must also clear local tokens and bounce to
  // the home page. OAuth-only users pass an empty string — the backend
  // skips the password check when no local hash exists.
  deleteMe: async (password: string): Promise<void> => {
    await ApiClient.delete('/users/me', { password });
  },
};
